import Vue from 'vue'
import ApiService from '@/common/api.service'

export const state = {
  /*
   * eventPoolsSelectionSets is a "grouping" of multiple pools that are
   * selected. For example, one might wish to have a "global" set of
   * selected pools that is persistent across all screens or one
   * might want to create a set of selected pools for a particular
   * individual/group of widgets.
   */
  eventPoolsSelectionSets: {},
  client_id: null,
  queryByClient: null
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns the sets of selected pools.
   *
   * @param state Object
   *
   * @return Object
   */
  eventPoolsSelectionSets (state) {
    return state.eventPoolsSelectionSets
  },

  /**
   * Returns either a client object or null depending on whether
   * queries should be made by client.
   *
   * @param state
   *
   * @return {Object|null}
   */
  getClient (state) {
    return state.queryByClient
  }
}

export const actions = {
  /**
   * Query/find one or more pools.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    const queryByClient = context.getters['getClient']
    if (queryByClient !== null) {
      return ApiService
        .get(`events/pools/by-client/${queryByClient.id}`, params)
        .then((response) => {
          return response
        })
    } else {
      return ApiService
        .get(`events/pools`, params)
        .then((response) => {
          return response
        })
    }
  },

  /**
   * Query/find one or more pools by client.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryByClient (context, params) {
    const clientId = context.getters['get'].client_id
    return ApiService
      .get(`events/pools/by-client/${clientId}`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Add a map or array of object pools that have been selected within
   * the specified set.
   *
   * @param state    Object
   * @param set      String
   * @param pools Array|Map
   */
  addSelectedPools (state, { set, pools }) {
    if (!state.eventPoolsSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.eventPoolsSelectionSets, set, {})
    }

    pools.forEach(pool => {
      Vue.set(state.eventPoolsSelectionSets[set], pool.id, pool)
    })
  },

  /**
   * Set a single selected pool, all other selected pools
   * in the set will be cleared.
   *
   * @param state  Object
   * @param set    String
   * @param pool Object
   */
  setSelectedPool (state, { set, pool }) {
    Vue.set(state.eventPoolsSelectionSets, set, {})
    Vue.set(state.eventPoolsSelectionSets[set], pool.id, pool)
  },

  /**
   * Removes an array of pools from the set's list of selected pools.
   * The array must consist of the id's of the pools that are no
   * longer selected.
   *
   * @param state    Object
   * @param set      String
   * @param pools Array
   */
  removeSelectedPools (state, { set, pools }) {
    if (state.eventPoolsSelectionSets.hasOwnProperty(set)) {
      pools.forEach(pool => {
        Vue.delete(state.eventPoolsSelectionSets[set], pool.id)
      })
    }
  },

  /**
   * Clears all selected pools.
   *
   * @param state  Object
   */
  clearAllSelectedPools (state) {
    Vue.set(state, 'eventPoolsSelectionSets', {})
  },

  /**
   * Clears all selected pools within a specified set.
   *
   * @param state  Object
   * @param set      String
   */
  clearSelectedPools (state, set) {
    if (state.eventPoolsSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.eventPoolsSelectionSets, set, {})
    }
  },

  setClientId (state, clientId) {
    state.client_id = clientId
  },

  /**
   * Sets the client for which the pools should be queried.
   *
   * @param state
   *
   * @param client Object|null
   */
  setClient (state, client) {
    state.queryByClient = client
  },

  /**
   * Clears all pools.
   *
   * @param state Object
   */
  purge (state) {
    state.eventPoolsSelectionSets = {}
    state.client_id = null
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/events/pools/findEventPoolsWidgetModule.js